import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PersonAdd, Email, Security, AdminPanelSettings } from '@mui/icons-material';

interface InviteUserProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface InviteFormData {
  name: string;
  email: string;
  role: string;
  permissions: string[];
  sendWelcomeEmail: boolean;
}

const InviteUser: React.FC<InviteUserProps> = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<InviteFormData>({
    name: '',
    email: '',
    role: 'admin',
    permissions: ['manage_profiles', 'view_profiles'],
    sendWelcomeEmail: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roles = [
    { value: 'super_admin', label: 'Super Admin', color: 'error' },
    { value: 'admin', label: 'Admin', color: 'primary' },
    { value: 'moderator', label: 'Moderator', color: 'secondary' },
  ];

  const availablePermissions = [
    { value: 'manage_profiles', label: 'Manage Service Providers' },
    { value: 'view_profiles', label: 'View Service Providers' },
    { value: 'approve_profiles', label: 'Approve Service Providers' },
    { value: 'manage_users', label: 'Manage Admin Users' },
    { value: 'view_analytics', label: 'View Analytics' },
    { value: 'system_settings', label: 'System Settings' },
  ];

  const rolePermissions = {
    super_admin: ['manage_profiles', 'view_profiles', 'approve_profiles', 'manage_users', 'view_analytics', 'system_settings'],
    admin: ['manage_profiles', 'view_profiles', 'approve_profiles', 'view_analytics'],
    moderator: ['view_profiles', 'approve_profiles'],
  };

  const handleRoleChange = (newRole: string) => {
    setFormData({
      ...formData,
      role: newRole,
      permissions: rolePermissions[newRole as keyof typeof rolePermissions] || [],
    });
  };

  const handlePermissionToggle = (permission: string) => {
    const updatedPermissions = formData.permissions.includes(permission)
      ? formData.permissions.filter(p => p !== permission)
      : [...formData.permissions, permission];
    
    setFormData({
      ...formData,
      permissions: updatedPermissions,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      setLoading(false);
      return;
    }

    if (!formData.email.trim()) {
      setError('Email is required');
      setLoading(false);
      return;
    }

    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    if (formData.permissions.length === 0) {
      setError('Please select at least one permission');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';
      
      const response = await fetch(`${API_BASE_URL}/api/admin/users/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          role: formData.role,
          permissions: formData.permissions,
          sendWelcomeEmail: formData.sendWelcomeEmail,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Show appropriate success message based on email status
        if (data.emailSent === false && formData.sendWelcomeEmail) {
          const errorMsg = data.emailError 
            ? `User created successfully, but invitation email failed: ${data.emailError}` 
            : 'User created successfully, but invitation email failed to send. Please check email configuration.';
          setError(errorMsg);
          console.error('Email error:', data.emailError);
        }
        onSuccess();
      } else {
        throw new Error(data.error || 'Failed to send invitation');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to send invitation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: '',
        email: '',
        role: 'admin',
        permissions: ['manage_profiles', 'view_profiles'],
        sendWelcomeEmail: true,
      });
      setError(null);
      onClose();
    }
  };

  const getRoleDescription = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'Full system access including user management and system settings';
      case 'admin':
        return 'Can manage service providers and view analytics';
      case 'moderator':
        return 'Can review and approve service provider applications';
      default:
        return '';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <PersonAdd color="primary" />
          <Typography variant="h6">Invite New Admin User</Typography>
        </Box>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Basic Information */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AdminPanelSettings />
              Basic Information
            </Typography>
            
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              placeholder="Enter admin's full name"
            />
            
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              margin="normal"
              required
              placeholder="Enter admin's email address"
              helperText="An invitation will be sent to this email address"
            />
          </Box>

          {/* Role Selection */}
          <Box mb={3}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Security />
              Role & Permissions
            </Typography>
            
            <FormControl fullWidth margin="normal">
              <InputLabel>Admin Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => handleRoleChange(e.target.value)}
                label="Admin Role"
              >
                {roles.map((role) => (
                  <MenuItem key={role.value} value={role.value}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip label={role.label} size="small" color={role.color as any} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
              {getRoleDescription(formData.role)}
            </Typography>

            {/* Custom Permissions */}
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Custom Permissions (Advanced)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {availablePermissions.map((permission) => (
                <FormControlLabel
                  key={permission.value}
                  control={
                    <Checkbox
                      checked={formData.permissions.includes(permission.value)}
                      onChange={() => handlePermissionToggle(permission.value)}
                      size="small"
                    />
                  }
                  label={permission.label}
                  sx={{ mr: 2, mb: 1 }}
                />
              ))}
            </Box>
          </Box>

          {/* Email Settings */}
          <Box mb={2}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Email />
              Notification Settings
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.sendWelcomeEmail}
                  onChange={(e) => setFormData({ ...formData, sendWelcomeEmail: e.target.checked })}
                />
              }
              label="Send welcome email with login instructions"
            />
            
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
              The user will receive an email with instructions to set up their account and initial password.
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            sx={{ backgroundColor: '#000', minWidth: 120 }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Send Invitation'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InviteUser;